"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2,
  Pause,
  Play,
  RotateCcw,
  Search,
  Volume2,
  Waves,
} from "lucide-react";
import { Select } from "@/components/ui/select";
import { api, type CartesiaVoice } from "@/lib/api";
import {
  TTS_EMOTION_MORE,
  TTS_EMOTION_PRIMARY,
  TTS_GENDER_OPTS,
  TTS_LANGUAGE_OPTS,
  clampTtsSpeed,
  clampTtsVolume,
  genderLabel,
  type VoiceDeliveryValue,
} from "@/lib/voice-catalog";

type Props = {
  value: VoiceDeliveryValue;
  onChange: (next: VoiceDeliveryValue) => void;
  layout?: "full" | "compact";
  clearLabel?: string;
  /** Shown under the clear action when a voice is selected. */
  clearHint?: string;
  /** Title when no voiceId is set. */
  unsetTitle?: string;
  /** Subtitle when no voiceId is set. */
  unsetHint?: string;
};

type ListCacheEntry = {
  voices: CartesiaVoice[];
  hasMore: boolean;
  nextPage: string | null;
  at: number;
};

const LIST_CACHE = new Map<string, ListCacheEntry>();
const LIST_CACHE_TTL_MS = 5 * 60 * 1000;

function listCacheKey(q: string, gender: string, language: string) {
  return `${q}\0${gender}\0${language}`;
}

function b64ToObjectUrl(data: string, contentType: string): string {
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: contentType || "audio/wav" }));
}

function normalizeVoice(raw: unknown): CartesiaVoice | null {
  if (!raw || typeof raw !== "object") return null;
  const v = raw as Record<string, unknown>;
  const id = String(v.id || "").trim();
  if (!id) return null;
  let name = String(v.name || v.display_name || v.voice_name || "").trim();
  let tagline = String(v.tagline || "").trim();
  if (!tagline && name.includes(" - ")) {
    const parts = name.split(" - ");
    name = parts[0].trim();
    tagline = parts.slice(1).join(" - ").trim();
  }
  if (!name) name = `Voice ${id.slice(0, 8)}`;
  return {
    id,
    name,
    tagline,
    description: String(v.description || "").trim(),
    gender: (v.gender as string) || null,
    language: (v.language as string) || null,
    locale: (v.locale as string) || null,
    accent: (v.accent as string) || null,
    country: (v.country as string) || null,
    hasPreview: v.hasPreview !== false,
    isPro: Boolean(v.isPro ?? v.is_pro),
  };
}

function voiceTitle(v: CartesiaVoice): string {
  return String(v.name || "").trim() || `Voice ${(v.id || "").slice(0, 8) || "—"}`;
}

export function VoicePicker({
  value,
  onChange,
  layout = "full",
  clearLabel = "Clear selection",
  clearHint,
  unsetTitle = "No voice selected",
  unsetHint = "Pick a voice below, or leave empty to use the platform fallback.",
}: Props) {
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [gender, setGender] = useState("");
  const [language, setLanguage] = useState("en");
  const [voices, setVoices] = useState<CartesiaVoice[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(layout === "full");
  const [moreEmotions, setMoreEmotions] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewUrlCacheRef = useRef<Map<string, string>>(new Map());
  const loadGenRef = useRef(0);

  useEffect(() => {
    const t = window.setTimeout(() => setQDebounced(q.trim()), 280);
    return () => window.clearTimeout(t);
  }, [q]);

  const stopPreview = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.removeAttribute("src");
      a.load();
    }
    setPlayingId(null);
  }, []);

  useEffect(() => () => {
    stopPreview();
    for (const url of previewUrlCacheRef.current.values()) URL.revokeObjectURL(url);
    previewUrlCacheRef.current.clear();
  }, [stopPreview]);

  const applyList = useCallback((entry: ListCacheEntry) => {
    setVoices(entry.voices);
    setHasMore(entry.hasMore);
    setNextPage(entry.nextPage);
  }, []);

  const loadVoices = useCallback(async (opts?: {
    append?: boolean;
    cursor?: string | null;
    force?: boolean;
  }) => {
    const append = Boolean(opts?.append);
    const key = listCacheKey(qDebounced, gender, language);
    const cached = LIST_CACHE.get(key);
    const fresh = cached && Date.now() - cached.at < LIST_CACHE_TTL_MS;
    // Ignore cache entries that somehow lost names (bad earlier response).
    const cacheUsable = Boolean(
      fresh
      && cached
      && cached.voices.length > 0
      && cached.voices.some((v) => String(v.name || "").trim()),
    );

    if (!append && !opts?.force && cacheUsable && cached) {
      applyList(cached);
      setLoading(false);
      setError(null);
      return;
    }

    if (!append && cacheUsable && cached && !opts?.force) {
      applyList(cached);
    }

    const gen = ++loadGenRef.current;
    if (append) setLoadingMore(true);
    else if (!cacheUsable) setLoading(true);
    setError(null);
    try {
      const res = await api.listCartesiaVoices({
        q: qDebounced || undefined,
        gender: gender || undefined,
        language: language || undefined,
        limit: 36,
        startingAfter: opts?.cursor || undefined,
      });
      if (gen !== loadGenRef.current) return;

      const incoming = (Array.isArray(res.voices) ? res.voices : [])
        .map(normalizeVoice)
        .filter((v): v is CartesiaVoice => Boolean(v));

      const merged = append
        ? [...(LIST_CACHE.get(key)?.voices || voices), ...incoming]
        : incoming;

      // De-dupe by id (append pages can overlap).
      const seen = new Set<string>();
      const deduped: CartesiaVoice[] = [];
      for (const v of merged) {
        if (seen.has(v.id)) continue;
        seen.add(v.id);
        deduped.push(v);
      }

      const entry: ListCacheEntry = {
        voices: deduped,
        hasMore: Boolean(res.hasMore),
        nextPage: res.nextPage || null,
        at: Date.now(),
      };
      LIST_CACHE.set(key, entry);
      applyList(entry);
    } catch (e) {
      if (gen !== loadGenRef.current) return;
      setError(e instanceof Error ? e.message : "Failed to load voices");
      if (!append && !cacheUsable) setVoices([]);
    } finally {
      if (gen === loadGenRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [qDebounced, gender, language, applyList, voices]);

  useEffect(() => {
    if (!libraryOpen) return;
    void loadVoices({ force: true });
  }, [libraryOpen, qDebounced, gender, language]); // eslint-disable-line react-hooks/exhaustive-deps

  async function playPreview(voiceId: string, e?: React.MouseEvent) {
    e?.preventDefault();
    e?.stopPropagation();
    if (!voiceId) return;
    if (playingId === voiceId) {
      stopPreview();
      return;
    }
    stopPreview();
    setPreviewLoadingId(voiceId);
    setError(null);
    try {
      let url = previewUrlCacheRef.current.get(voiceId);
      if (!url) {
        const preview = await api.getCartesiaVoicePreview(voiceId);
        url = b64ToObjectUrl(preview.data, preview.contentType);
        previewUrlCacheRef.current.set(voiceId, url);
      }
      if (!audioRef.current) audioRef.current = new Audio();
      const a = audioRef.current;
      a.src = url;
      a.onended = () => setPlayingId(null);
      a.onerror = () => {
        setPlayingId(null);
        setError("Preview playback failed");
      };
      await a.play();
      setPlayingId(voiceId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setPreviewLoadingId(null);
    }
  }

  function selectVoice(v: CartesiaVoice) {
    onChange({
      ...value,
      voiceId: v.id,
      voiceName: voiceTitle(v),
    });
  }

  const speed = clampTtsSpeed(value.ttsSpeed, 1);
  const volume = clampTtsVolume(value.ttsVolume, 1);
  const emotion = value.ttsEmotion || null;
  const selected = voices.find((v) => v.id === value.voiceId);
  const sampleNames = voices.slice(0, 3).map(voiceTitle).filter(Boolean);

  return (
    <div className={`voice-picker${layout === "compact" ? " voice-picker--compact" : ""}`}>
      <div className="voice-selected">
        <div className="voice-selected-mark" aria-hidden>
          <Waves size={18} />
        </div>
        <div className="voice-selected-copy">
          <span className="voice-selected-kicker">Voice · TTS</span>
          <b>{value.voiceName || selected?.name || (value.voiceId ? "Custom voice" : unsetTitle)}</b>
          <span>
            {value.voiceId
              ? [
                  genderLabel(selected?.gender),
                  selected?.locale || selected?.language,
                  emotion ? emotion : "emotion auto",
                  `${speed.toFixed(2)}×`,
                ].filter(Boolean).join(" · ")
              : unsetHint}
          </span>
        </div>
        <div className="voice-selected-actions">
          {value.voiceId ? (
            <button
              type="button"
              className="btn small"
              disabled={previewLoadingId === value.voiceId}
              onClick={(e) => void playPreview(value.voiceId!, e)}
            >
              {previewLoadingId === value.voiceId
                ? <Loader2 size={14} className="spin" />
                : playingId === value.voiceId ? <Pause size={14} /> : <Play size={14} />}
              Preview
            </button>
          ) : null}
          <button
            type="button"
            className="btn small primary"
            onClick={() => setLibraryOpen((o) => !o)}
          >
            {libraryOpen ? "Hide library" : value.voiceId ? "Change voice" : "Pick voice"}
          </button>
        </div>
      </div>

      {value.voiceId ? (
        <div className="voice-clear-row voice-clear-row--selected">
          <button
            type="button"
            className="linkish"
            onClick={() => onChange({ ...value, voiceId: null, voiceName: null })}
          >
            {clearLabel}
          </button>
          {clearHint ? <p className="voice-clear-hint">{clearHint}</p> : null}
        </div>
      ) : null}

      {libraryOpen && (
        <div className="voice-library">
          <div className="voice-filters">
            <label className="voice-search">
              <Search size={14} aria-hidden />
              <input
                className="form-control"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, description, or ID…"
                aria-label="Search voices"
              />
            </label>
            <Select
              triggerClassName="form-control"
              size="sm"
              searchable={false}
              ariaLabel="Filter by gender"
              value={gender}
              onChange={(v) => setGender(v ?? "")}
              options={TTS_GENDER_OPTS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <Select
              triggerClassName="form-control"
              size="sm"
              searchable={false}
              ariaLabel="Filter by language"
              value={language}
              onChange={(v) => setLanguage(v ?? "")}
              options={TTS_LANGUAGE_OPTS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          </div>

          {error && <p className="voice-error" role="alert">{error}</p>}

          {!loading && voices.length > 0 && (
            <p className="voice-count" style={{ margin: 0, color: "#526985", fontSize: 11, fontWeight: 600 }}>
              {voices.length} voice{voices.length === 1 ? "" : "s"}
              {sampleNames.length ? ` · e.g. ${sampleNames.join(", ")}` : ""}
            </p>
          )}

          {loading && voices.length === 0 ? (
            <div className="voice-empty"><Loader2 className="spin" size={18} /> Loading voices…</div>
          ) : voices.length === 0 ? (
            <div className="voice-empty">No voices match. Try clearing filters or check the Cartesia API key.</div>
          ) : (
            <ul className="voice-list" role="listbox" aria-label="Voice library">
              {voices.map((v) => {
                const active = v.id === value.voiceId;
                const playing = playingId === v.id;
                const loadingPrev = previewLoadingId === v.id;
                const title = voiceTitle(v);
                const meta = [
                  genderLabel(v.gender),
                  v.locale || v.language || null,
                  v.isPro ? "Pro" : null,
                ].filter((x) => x && x !== "—");
                return (
                  <li
                    key={v.id}
                    role="option"
                    aria-selected={active}
                    className={`voice-row${active ? " is-active" : ""}${playing ? " is-playing" : ""}`}
                  >
                    <div className="voice-row-copy">
                      <div
                        className="voice-row-name"
                        style={{ color: "#10233f", fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}
                      >
                        {title}
                      </div>
                      {v.tagline ? (
                        <div style={{ color: "#0968f2", fontSize: 11, fontWeight: 600 }}>{v.tagline}</div>
                      ) : null}
                      {meta.length > 0 ? (
                        <div className="voice-row-meta" style={{ color: "#617890", fontSize: 11 }}>
                          {meta.join(" · ")}
                        </div>
                      ) : null}
                      {v.description ? (
                        <div
                          className="voice-row-desc"
                          style={{ color: "#6a7d94", fontSize: 11, lineHeight: 1.35 }}
                        >
                          {v.description}
                        </div>
                      ) : null}
                    </div>
                    <div className="voice-row-actions">
                      <button
                        type="button"
                        className={`btn small${playing ? " voice-preview-on" : ""}`}
                        disabled={loadingPrev}
                        style={{ minWidth: 88 }}
                        onClick={(e) => void playPreview(v.id, e)}
                      >
                        {loadingPrev
                          ? <Loader2 size={14} className="spin" />
                          : playing ? <Pause size={14} /> : <Play size={14} />}
                        {playing ? "Pause" : "Preview"}
                      </button>
                      <button
                        type="button"
                        className={`btn small${active ? " primary" : ""}`}
                        style={{ minWidth: 72 }}
                        onClick={() => selectVoice(v)}
                      >
                        {active ? "Selected" : "Use"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {hasMore && (
            <div className="voice-more">
              <button
                type="button"
                className="btn"
                disabled={loadingMore}
                onClick={() => void loadVoices({ append: true, cursor: nextPage })}
              >
                {loadingMore ? <Loader2 size={14} className="spin" /> : null}
                Load more
              </button>
            </div>
          )}
        </div>
      )}

      <div className="voice-delivery">
        <div className="voice-delivery-head">
          <Volume2 size={14} aria-hidden />
          <b>Delivery</b>
          <span>Speed, loudness, and default emotion for Sonic 3.5</span>
          <button
            type="button"
            className="linkish"
            onClick={() => onChange({
              ...value,
              ttsSpeed: 1,
              ttsVolume: 1,
              ttsEmotion: null,
            })}
            title="Reset speed, volume, and emotion"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>

        <label className="form-label voice-slider">
          <span>Speed <em>{speed.toFixed(2)}×</em></span>
          <div className="agent-timeout-row">
            <input
              type="range"
              min={0.6}
              max={1.5}
              step={0.05}
              value={speed}
              onChange={(e) => onChange({ ...value, ttsSpeed: clampTtsSpeed(e.target.value) })}
            />
            <input
              className="form-control"
              type="number"
              min={0.6}
              max={1.5}
              step={0.05}
              value={speed}
              onChange={(e) => onChange({ ...value, ttsSpeed: clampTtsSpeed(e.target.value) })}
              style={{ width: 72 }}
            />
          </div>
        </label>

        <label className="form-label voice-slider">
          <span>Volume <em>{volume.toFixed(2)}</em></span>
          <div className="agent-timeout-row">
            <input
              type="range"
              min={0.5}
              max={2}
              step={0.05}
              value={volume}
              onChange={(e) => onChange({ ...value, ttsVolume: clampTtsVolume(e.target.value) })}
            />
            <input
              className="form-control"
              type="number"
              min={0.5}
              max={2}
              step={0.05}
              value={volume}
              onChange={(e) => onChange({ ...value, ttsVolume: clampTtsVolume(e.target.value) })}
              style={{ width: 72 }}
            />
          </div>
        </label>

        <div className="voice-emotion">
          <div className="voice-emotion-label">
            <b>Emotion</b>
            <span>English only · omit for auto (model reads the line)</span>
          </div>
          <div className="voice-emotion-chips" role="group" aria-label="Primary emotions">
            <button
              type="button"
              className={`chip emotion-chip${!emotion ? " on" : ""}`}
              onClick={() => onChange({ ...value, ttsEmotion: null })}
            >
              auto
            </button>
            {TTS_EMOTION_PRIMARY.map((em) => (
              <button
                key={em}
                type="button"
                className={`chip emotion-chip emotion-${em}${emotion === em ? " on" : ""}`}
                onClick={() => onChange({ ...value, ttsEmotion: em })}
              >
                {em}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="linkish voice-emotion-more-toggle"
            onClick={() => setMoreEmotions((v) => !v)}
          >
            {moreEmotions ? "Hide more emotions" : "Show more emotions"}
          </button>
          {moreEmotions && (
            <div className="voice-emotion-chips voice-emotion-chips--more" role="group" aria-label="More emotions">
              {TTS_EMOTION_MORE.map((em) => (
                <button
                  key={em}
                  type="button"
                  className={`chip emotion-chip${emotion === em ? " on" : ""}`}
                  onClick={() => onChange({ ...value, ttsEmotion: em })}
                >
                  {em}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
